import React, { useState, useContext, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import ConfirmPrompt from '../General/ConfirmPrompt';
import './OfficeSpaceBuilder.scss';

import { OfficeContext } from './Office';
import { createOfficeSpace, updateOfficeSpace, deleteOfficeSpace, updateOfficeFloor } from '../../api/office';
import { OFFICE_SPACE_TYPES, SPACE_TYPE_LABELS, ROOM_NAMES, isRoomType } from '../../helpers/office_helpers';

const TYPES = Object.values(OFFICE_SPACE_TYPES);

const OfficeSpaceBuilder = () => {
  const ctx = useContext(OfficeContext);
  const { floors, spaces, selectedFloorId, setSelectedFloorId, selectedFloor, loadAvailability, refreshFloors } = ctx;

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [isSaving, setIsSaving] = useState(false);

  function getEmptyForm() {
    return { name: '', type: OFFICE_SPACE_TYPES.CONFERENCE_ROOM, capacity: 4, min_scope: '', floor_plan_x: 0.5, floor_plan_y: 0.5, floor_plan_w: 0.08, floor_plan_h: 0.06, max_concurrent_bookings: 1, persistent_zoom_room_url: '' };
  }

  const updateForm = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const nameOptions = ROOM_NAMES[formData.type] || [];

  const handleSave = async () => {
    if (!formData.name) return window.ALERT.error('Name is required.');
    setIsSaving(true);
    try {
      const payload = { ...formData, OfficeFloor_id: selectedFloorId };
      if (formData.type === OFFICE_SPACE_TYPES.OPEN_SEATING) {
        payload.floor_plan_x = null;
        payload.floor_plan_y = null;
        payload.floor_plan_w = null;
        payload.floor_plan_h = null;
        payload.max_concurrent_bookings = payload.capacity || 50;
      }
      if (editingId) {
        await updateOfficeSpace(editingId, payload);
        window.ALERT.success('Space updated.');
      } else {
        await createOfficeSpace(payload);
        window.ALERT.success('Space created.');
      }
      setIsAdding(false);
      setEditingId(null);
      setFormData(getEmptyForm());
      loadAvailability();
    } catch (err) {
      window.ALERT.error(typeof err === 'string' ? err : 'Failed to save space.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = id => {
    confirmAlert({
      title: 'Delete this space?',
      message: 'This action cannot be undone.',
      buttons: [
        { label: 'Cancel' },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await deleteOfficeSpace(id);
              window.ALERT.success('Space deleted.');
              loadAvailability();
            } catch (err) {
              window.ALERT.error('Failed to delete space.');
            }
          }
        }
      ]
    });
  };

  const startEdit = space => {
    setFormData({
      name: space.name, type: space.type, capacity: space.capacity, min_scope: space.min_scope || '',
      floor_plan_x: space.floor_plan_x || 0.5, floor_plan_y: space.floor_plan_y || 0.5,
      floor_plan_w: space.floor_plan_w || 0.08, floor_plan_h: space.floor_plan_h || 0.06,
      max_concurrent_bookings: space.max_concurrent_bookings || 1,
      persistent_zoom_room_url: space.persistent_zoom_room_url || ''
    });
    setEditingId(space.id);
    setIsAdding(true);
  };

  const toggleEdit = space => {
    if (editingId === space.id) {
      setEditingId(null);
      setIsAdding(false);
    } else {
      startEdit(space);
    }
  };

  const handleDragUpdate = useCallback(async (spaceId, updates) => {
    try {
      await updateOfficeSpace(spaceId, updates);
      loadAvailability();
    } catch (err) {
      window.ALERT.error('Failed to update position.');
    }
  }, [loadAvailability]);

  const floorSpaces = spaces.filter(s => s.type !== 'open_seating');

  const handleUpdateImage = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Floor plan image URL'
          isSingleLine
          preloaded={selectedFloor?.image_url || ''}
          placeholder='https://...'
          onCancel={onClose}
          onSubmit={async url => {
            onClose();
            try {
              await updateOfficeFloor(selectedFloorId, { image_url: url || null });
              window.ALERT.success('Floor image updated.');
              await refreshFloors();
            } catch {
              window.ALERT.error('Failed to update floor image.');
            }
          }}
        />
      )
    });
  };

  return (
    <div className='office-space-builder-outer'>
      <div className='builder-header'>
        <h2 className='builder-title'>Space Management</h2>
        <button className='builder-add-btn' onClick={() => { setIsAdding(true); setEditingId(null); setFormData(getEmptyForm()); }}>+ Add Space</button>
      </div>

      <div className='builder-floor-tabs-row'>
        <div className='builder-floor-tabs'>
          {floors.map(floor => (
            <button key={floor.id} className={cn('builder-floor-tab', { active: selectedFloorId === floor.id })} onClick={() => setSelectedFloorId(floor.id)}>
              {floor.name || `Floor ${floor.floor}`}
            </button>
          ))}
        </div>
        <button className='builder-image-btn' onClick={handleUpdateImage}>
          {selectedFloor?.image_url ? 'Change Image' : 'Set Image'}
        </button>
      </div>

      {/* Interactive floor plan with draggable/resizable spaces */}
      {selectedFloor?.image_url && (
        <DraggableFloorPlan
          imageUrl={selectedFloor.image_url}
          spaces={floorSpaces}
          editingId={editingId}
          onDragEnd={handleDragUpdate}
          onClickSpace={toggleEdit}
        />
      )}

      {/* Space list with inline edit */}
      <div className='builder-spaces-list'>
        {floorSpaces.map(space => (
          <div key={space.id} className={cn('builder-space-row-wrap', { expanded: editingId === space.id })}>
            <div className={cn('builder-space-row', { editing: editingId === space.id })} onClick={() => toggleEdit(space)}>
              <div className='builder-space-info'>
                <span className='builder-space-name'>{space.name}</span>
                <span className='builder-space-type'>{SPACE_TYPE_LABELS[space.type]}</span>
                <span className='builder-space-capacity'>{space.capacity} seats</span>
                {space.persistent_zoom_room_url && <span className='builder-space-zoom'>Zoom</span>}
              </div>
              <div className='builder-space-actions'>
                <button className='builder-delete-btn' onClick={e => { e.stopPropagation(); handleDelete(space.id); }}>Delete</button>
              </div>
            </div>

            {editingId === space.id && isAdding && (
              <SpaceForm formData={formData} updateForm={updateForm} nameOptions={nameOptions} isSaving={isSaving} onSave={handleSave} onCancel={() => { setEditingId(null); setIsAdding(false); }} isNew={false} />
            )}
          </div>
        ))}

        {/* New space form at bottom */}
        {isAdding && !editingId && (
          <div className='builder-space-row-wrap expanded new'>
            <SpaceForm formData={formData} updateForm={updateForm} nameOptions={nameOptions} isSaving={isSaving} onSave={handleSave} onCancel={() => setIsAdding(false)} isNew />
          </div>
        )}

        {!floorSpaces.length && !isAdding && <div className='builder-empty'>No spaces on this floor.</div>}
      </div>
    </div>
  );
};

const SpaceForm = ({ formData, updateForm, nameOptions, isSaving, onSave, onCancel, isNew }) => (
  <div className='builder-form'>
    <div className='builder-form-grid'>
      <label>
        Type
        <select value={formData.type} onChange={e => { updateForm('type', e.target.value); updateForm('name', ''); }}>
          {TYPES.map(t => <option key={t} value={t}>{SPACE_TYPE_LABELS[t]}</option>)}
        </select>
      </label>
      <label>
        Name
        {nameOptions.length > 0 ? (
          <div className='name-select-row'>
            <select value={nameOptions.includes(formData.name) ? formData.name : '__custom'} onChange={e => {
              if (e.target.value === '__custom') {
                confirmAlert({
                  customUI: ({ onClose }) => (
                    <ConfirmPrompt
                      header='Enter a custom name'
                      isSingleLine
                      placeholder='Room name'
                      onCancel={onClose}
                      onSubmit={val => { onClose(); if (val) updateForm('name', val); }}
                    />
                  )
                });
              } else {
                updateForm('name', e.target.value);
              }
            }}>
              <option value=''>Select...</option>
              {nameOptions.map(n => <option key={n} value={n}>{n}</option>)}
              <option value='__custom'>Custom...</option>
            </select>
            {formData.name && !nameOptions.includes(formData.name) && (
              <span className='custom-name-badge'>{formData.name}</span>
            )}
          </div>
        ) : (
          <input value={formData.name} onChange={e => updateForm('name', e.target.value)} placeholder='e.g. Desk 9-14' />
        )}
      </label>
      <label>
        Capacity
        <input type='number' value={formData.capacity} onChange={e => updateForm('capacity', parseInt(e.target.value) || 1)} min={1} />
      </label>
      <label>
        Min Scope
        <select value={formData.min_scope} onChange={e => updateForm('min_scope', e.target.value)}>
          <option value=''>None</option>
          <option value='office_exec'>Executive+</option>
          <option value='office_manager'>Manager+</option>
          <option value='office_admin'>Admin only</option>
        </select>
      </label>
      <label className='full-width'>
        Zoom / Meeting URL
        <input value={formData.persistent_zoom_room_url} onChange={e => updateForm('persistent_zoom_room_url', e.target.value)} placeholder='https://zoom.us/j/...' />
      </label>
    </div>
    <div className='builder-form-actions'>
      <button className='builder-save-btn' onClick={onSave} disabled={isSaving}>{isSaving ? 'Saving...' : isNew ? 'Create' : 'Update'}</button>
      <button className='builder-cancel-btn' onClick={onCancel}>Cancel</button>
    </div>
  </div>
);

const DraggableFloorPlan = ({ imageUrl, spaces, editingId, onDragEnd, onClickSpace }) => {
  const containerRef = useRef(null);
  const [dragState, setDragState] = useState(null); // { spaceId, mode: 'move'|'resize', startMouseX, startMouseY, startX, startY, startW, startH }

  const getRelativePos = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { rx: 0, ry: 0 };
    return {
      rx: (e.clientX - rect.left) / rect.width,
      ry: (e.clientY - rect.top) / rect.height
    };
  }, []);

  const handleMouseDown = useCallback((e, space, mode) => {
    e.stopPropagation();
    e.preventDefault();
    const { rx, ry } = getRelativePos(e);
    setDragState({
      spaceId: space.id,
      mode,
      startMouseX: rx,
      startMouseY: ry,
      startX: space.floor_plan_x,
      startY: space.floor_plan_y,
      startW: space.floor_plan_w,
      startH: space.floor_plan_h
    });
  }, [getRelativePos]);

  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;
    const { rx, ry } = getRelativePos(e);
    const dx = rx - dragState.startMouseX;
    const dy = ry - dragState.startMouseY;

    setDragState(prev => ({ ...prev, currentDX: dx, currentDY: dy }));
  }, [dragState, getRelativePos]);

  const handleMouseUp = useCallback(() => {
    if (!dragState || dragState.currentDX === undefined) {
      setDragState(null);
      return;
    }

    const { spaceId, mode, startX, startY, startW, startH, currentDX, currentDY } = dragState;
    let updates;

    if (mode === 'move') {
      updates = {
        floor_plan_x: Math.max(0, Math.min(1, startX + currentDX)),
        floor_plan_y: Math.max(0, Math.min(1, startY + currentDY))
      };
    } else {
      updates = {
        floor_plan_w: Math.max(0.02, Math.min(0.4, startW + currentDX)),
        floor_plan_h: Math.max(0.02, Math.min(0.4, startH + currentDY))
      };
    }

    onDragEnd(spaceId, updates);
    setDragState(null);
  }, [dragState, onDragEnd]);

  useEffect(() => {
    if (!dragState) return;
    const onMove = e => handleMouseMove(e);
    const onUp = () => handleMouseUp();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragState, handleMouseMove, handleMouseUp]);

  const getSpaceStyle = (space) => {
    let x = space.floor_plan_x;
    let y = space.floor_plan_y;
    let w = space.floor_plan_w || 0.06;
    let h = space.floor_plan_h || 0.06;

    if (dragState?.spaceId === space.id) {
      const dx = dragState.currentDX || 0;
      const dy = dragState.currentDY || 0;
      if (dragState.mode === 'move') {
        x = dragState.startX + dx;
        y = dragState.startY + dy;
      } else {
        w = Math.max(0.02, dragState.startW + dx);
        h = Math.max(0.02, dragState.startH + dy);
      }
    }

    return {
      left: `${x * 100}%`,
      top: `${y * 100}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`
    };
  };

  return (
    <div className='builder-floor-plan' ref={containerRef}>
      <img src={imageUrl} alt='Floor plan' className='builder-floor-plan-image' draggable={false} />
      {spaces.map(space => {
        if (space.floor_plan_x == null) return null;
        const isDragging = dragState?.spaceId === space.id;
        const isEditing = editingId === space.id;

        return (
          <div
            key={space.id}
            className={cn('builder-space-overlay', {
              dragging: isDragging,
              editing: isEditing,
              room: isRoomType(space),
              desk: space.type === 'desk'
            })}
            style={getSpaceStyle(space)}
            onMouseDown={e => handleMouseDown(e, space, 'move')}
            onDoubleClick={() => onClickSpace(space)}
          >
            <span className='builder-space-overlay-label'>{space.name}</span>
            <div
              className='builder-resize-handle'
              onMouseDown={e => handleMouseDown(e, space, 'resize')}
            />
          </div>
        );
      })}
    </div>
  );
};

export default OfficeSpaceBuilder;
